import React, { useMemo } from 'react';
import { WEB_MODAL_MAX_WIDTH } from '../../../theme/webLayoutWidths';
import { View, StyleSheet, Modal, useWindowDimensions } from 'react-native';
import { BlurView } from '@react-native-community/blur';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import FontAwesome from '@react-native-vector-icons/fontawesome/static';
import Button from '../../../components/Reusables/Button';
import { AppSnackbar } from '../../../services/AppSnackbar';
import { fs, iconSize, s, v, formatMoney } from '../../../utils/libs';
import { GTFonts, theme } from '../../../theme';
import DemoAttentionPulse from './DemoAttentionPulse';
import Typography from '../../../components/Reusables/Typography';
import { shouldUseWebLayout } from '../../../utils/layoutUtils';
import { formatDisplayDateTime } from '../../../utils/formatDisplayDateTime';

interface DemoPaymentModalProps {
  visible: boolean;
  paymentInfo: any;
  onBackToShop: () => void;
}

const DemoPaymentModal: React.FC<DemoPaymentModalProps> = ({
  visible,
  paymentInfo,
  onBackToShop,
}) => {
  const { width, height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const useConstrainedModal = shouldUseWebLayout(width, height);
  const modalInnerWidth = useMemo(() => {
    if (useConstrainedModal) {
      const outer = Math.min(width * 0.9, WEB_MODAL_MAX_WIDTH);
      // modalView has horizontal padding `s(8)` + buttons container has `s(16)` padding each side
      return Math.max(240, outer - s(16) - s(32));
    }
    return width * 0.8 - s(32);
  }, [width, useConstrainedModal]);

  if (
    !paymentInfo ||
    !paymentInfo?.payment ||
    !paymentInfo?.payment?.bp_payment_updated_at
  ) {
    return null;
  }

  const latestPayment =
    paymentInfo?.payment?.bp_payments &&
    paymentInfo?.payment?.bp_payments?.length > 0
      ? paymentInfo?.payment?.bp_payments[
          paymentInfo?.payment?.bp_payments.length - 1
        ]
      : null;

  const latestRefund =
    paymentInfo?.payment?.bp_payment_refunds &&
    paymentInfo?.payment?.bp_payment_refunds?.length > 0
      ? paymentInfo?.payment?.bp_payment_refunds[
          paymentInfo?.payment?.bp_payment_refunds.length - 1
        ]
      : null;

  return (
    <Modal
      animationType="slide"
      visible={visible}
      transparent={true}
      statusBarTranslucent={true}
      navigationBarTranslucent={true}>
      <View style={styles.centeredView}>
        <BlurView
          blurAmount={1}
          blurType="dark"
          style={StyleSheet.absoluteFill}
        />
        <View
          style={[
            styles.modalView,
            useConstrainedModal && styles.modalViewConstrained,
          ]}>
            <View style={{ marginBottom: v(10) }}>
              <FontAwesome
                name="check-circle"
                size={iconSize(60)}
                color={theme.colors.success}
              />
            </View>
            <View
              style={{
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: v(10),
              }}>
              <Typography
                style={{
                  fontSize: fs(24),
                  color: theme.colors.success,
                  marginBottom: v(10),
                }}>
                Transaction Successful
              </Typography>
              <Typography
                style={{
                  fontSize: fs(16),
                  lineHeight: fs(20),
                  fontFamily: GTFonts.regular,
                  marginVertical: v(10),
                }}>
                {formatDisplayDateTime(
                  paymentInfo?.payment?.bp_payment_updated_at,
                  '',
                )}
              </Typography>
            </View>
            <View>
              <View style={styles.section}>
                <Typography style={styles.text}>Pay Method</Typography>
                <Typography style={styles.text2}>
                  {latestPayment?.method ||
                    paymentInfo?.payment?.bp_payment_method}
                </Typography>
              </View>
              <View style={styles.section}>
                <Typography style={styles.text}>Total Bill</Typography>
                <Typography style={styles.text2}>
                  KSh {formatMoney(paymentInfo?.payment.bp_payment_amount)}
                </Typography>
              </View>
              <View style={styles.section}>
                <Typography style={styles.text}>Current Bill</Typography>
                <Typography style={styles.text2}>
                  KSh{'\u00A0'}
                  {formatMoney(paymentInfo?.payment?.bp_current_bill_amount)}
                </Typography>
              </View>
              <View style={styles.section}>
                <Typography style={styles.text}>Amount Received</Typography>
                <Typography style={styles.text2}>
                  KSh {formatMoney(paymentInfo?.payment.bp_amount_paid)}
                </Typography>
              </View>
              <View style={styles.section}>
                <Typography style={styles.text}>Balance Remaining</Typography>
                <Typography style={styles.text2}>
                  KSh{'\u00A0'}
                  {formatMoney(paymentInfo?.payment?.bp_payment_amount_due)}
                </Typography>
              </View>
              <View style={styles.section}>
                <Typography style={styles.text}>Change</Typography>
                <Typography style={styles.text2}>
                  KSh{'\u00A0'}
                  {formatMoney(latestRefund?.amount || 0)}
                </Typography>
              </View>
            </View>

            <View
              style={[styles.buttons, { marginBottom: v(10) + insets.bottom }]}>
              <Button
                text="Print Receipt"
                rippleColor={theme.colors.textGray600}
                fontSize={fs(16)}
                width={modalInnerWidth}
                icon="print"
                height={v(46)}
                borderRadius={s(8)}
                paddingHorizontal={s(18)}
                onPress={() => {
                  AppSnackbar.show(
                    'Printing is not available in demo mode, Please create a BizPlus POS account to use this feature.',
                    'info',
                    { durationMs: 7500 },
                  );
                }}
                backgroundColor={theme.colors.primary}
              />
              <DemoAttentionPulse active={visible}>
                <Button
                  text="Finish Demo"
                  rippleColor={theme.colors.textGray600}
                  width={modalInnerWidth}
                  fontSize={fs(16)}
                  icon={''}
                  height={v(46)}
                  paddingHorizontal={s(18)}
                  borderRadius={s(8)}
                  backgroundColor={theme.colors.success}
                  onPress={onBackToShop}
                />
              </DemoAttentionPulse>
            </View>
        </View>
      </View>
    </Modal>
  );
};

export default DemoPaymentModal;

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  modalView: {
    margin: s(5),
    marginHorizontal: s(5),
    backgroundColor: 'white',
    borderRadius: s(8),
    padding: s(8),
    alignItems: 'center',
    shadowColor: theme.colors.shadow,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: s(4),
    elevation: 5,
  },
  modalViewConstrained: {
    maxWidth: WEB_MODAL_MAX_WIDTH,
    width: '90%',
  },
  section: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: v(10),
  },
  text: {
    marginRight: s(30),
    fontSize: fs(18),
    lineHeight: fs(20),
    fontFamily: GTFonts.regular,
  },
  text2: {
    fontSize: fs(16),
    lineHeight: fs(18),
    fontFamily: GTFonts.bold,
    color: theme.colors.text,
  },
  buttons: {
    marginTop: v(20),
    gap: s(12),
    paddingHorizontal: s(16),
    flexDirection: 'column',
    alignItems: 'center',
  },
});
